import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  loadSessions,
  createSession,
  deleteSession as deleteSessionApi,
  restoreDeletedSession,
  loadConversation,
  updateSessionTitleById, // Existing function
  updateSessionTitleByUser, // New function for user-triggered updates
} from "../service/apiService";
import { useUser } from "./UserContext";
import { toast } from "sonner"; // Import toast notifications

interface Session {
  id: string;
  title: string;
  model_id?: string
  hat_id?: string
  knowledge_base_id?: string
  createdAt: string;
  updatedAt: string;
  session_type?: string;
  files?: any[];
}

interface SessionContextProps {
  sessionId: string;
  setSessionId: (sessionId: string) => void;
  conversations: any[];
  setConversations: (conversations: any[]) => void;
  sessions: Session[];
  setSessions: React.Dispatch<React.SetStateAction<Session[]>>;
  loading: boolean;
  loadingConversation: boolean;
  error: any;
  fetchAndSetSessions: (user_id: string | undefined) => Promise<void>;
  createSessionId: (
    modelId?: string,
    hatId?: string,
    title?: string) => Promise<string>;
  deleteSession: (selectedSessionId: string) => Promise<void>;
  updateSessionTitle: (sessionId: string, force?: boolean) => Promise<void>;
  updateSessionTitleByUser: (
    sessionId: string,
    newTitle: string
  ) => Promise<void>; // New function for user updates
  loadSessionConversation: (sessionId: string) => Promise<any[]>;
  deletingSessionId: string | null;
}

const SessionContext = createContext<SessionContextProps | undefined>(
  undefined
);

export const useSession = (): SessionContextProps => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
};

interface SessionProviderProps {
  children: React.ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({
  children,
}) => {
  const [sessionId, setSessionId] = useState<string>("");
  const [conversations, setConversations] = useState<any[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [error, setError] = useState<any>(null);
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(
    null
  );

  const { userId } = useUser();

  const fetchAndSetSessions = useCallback(
    async (user_id: string | undefined) => {
      if (!user_id) return;
      try {
        setLoading(true);
        const data = await loadSessions();
        const processedData = data.map((session: Session) => ({
          ...session,
          title: session.title?.trim()
            ? session.title
            : "Untitled Conversation",
        }));
        const sortedData = processedData.sort(
          (a: any, b: any) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
        setSessions(sortedData);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (userId) {
      fetchAndSetSessions(userId);
    }
  }, [userId, fetchAndSetSessions]);

  const createSessionId = useCallback(
    async (
      modelId?: string,
      hatId?: string,
      title?: string
    ): Promise<string> => {
      try {
        const response = await createSession(modelId, hatId, title);
        setSessionId(response.id);
        return response.id;
      } catch (error) {
        console.error("Failed to create session", error);
        throw new Error("Failed to initialize session");
      }
    },
    []
  );

  const deleteSession = useCallback(
    async (selectedSessionId: string): Promise<void> => {
      try {
        setDeletingSessionId(selectedSessionId);
        const result = await deleteSessionApi(selectedSessionId);
        setSessions((prevSessions) =>
          prevSessions.filter((session) => session.id !== selectedSessionId)
        );
        if (sessionId === selectedSessionId) {
          setSessionId("");
          setConversations([]);
        }
        if (result?.purge_at) {
          // The delete waits in Recently deleted; Undo brings it straight back.
          toast.success("Conversation deleted", {
            description: "You can restore it from Chat History › Recently deleted for 24 hours.",
            duration: 10000,
            action: {
              label: "Undo",
              onClick: async () => {
                try {
                  await restoreDeletedSession(selectedSessionId);
                  toast.success("Conversation restored");
                  await fetchAndSetSessions(userId);
                } catch (error) {
                  console.error("Failed to restore conversation", error);
                  toast.error("Failed to restore conversation");
                }
              },
            },
          });
        } else {
          toast.success("Conversation session deleted successfully!");
        }
      } catch (error) {
        console.error("Failed to delete conversation session", error);
        toast.error("Failed to delete conversation session. Please try again.");
        throw new Error("Failed to delete conversation session");
      } finally {
        setDeletingSessionId(null);
      }
    },
    [sessionId, fetchAndSetSessions, userId]
  );

  const updateSessionTitle = useCallback(
    async (sessionId: string, force: boolean = false): Promise<void> => {
      try {
        await updateSessionTitleById(sessionId, force);
        await fetchAndSetSessions(userId);
      } catch (error) {
        console.error("Failed to update session title", error);
      }
    },
    [fetchAndSetSessions, userId]
  );

  // New function to update the session title triggered by the user
  const updateSessionTitleByUser = useCallback(
    async (sessionId: string, newTitle: string): Promise<void> => {
      try {
        await updateSessionTitleByUser(sessionId, newTitle); // API call

        // Optimistically update the session title in state
        setSessions((prevSessions) =>
          prevSessions.map((session) =>
            session.id === sessionId ? { ...session, title: newTitle } : session
          )
        );

        // Show success toast notification
        toast.success("Session title updated successfully!");
      } catch (error) {
        console.error("Failed to update session title", error);
        toast.error("Failed to update session title. Please try again.");
      }
    },
    [setSessions]
  );

  const loadSessionConversation = useCallback(
    async (sessionId: string): Promise<any[]> => {
      try {
        setLoadingConversation(true);
        const conversation = await loadConversation(sessionId);
        setConversations(conversation);
        toast.success("Conversation loaded successfully!");
        return conversation;
      } catch (error) {
        console.error("Failed to load conversation", error);
        setConversations([]);
        return [];
      } finally {
        setLoadingConversation(false);
      }
    },
    []
  );

  return (
    <SessionContext.Provider
      value={{
        sessionId,
        setSessionId,
        conversations,
        setConversations,
        sessions,
        setSessions,
        loading,
        loadingConversation,
        error,
        fetchAndSetSessions,
        createSessionId,
        deleteSession,
        updateSessionTitle,
        updateSessionTitleByUser, // Include the user-triggered session title update
        loadSessionConversation,
        deletingSessionId,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};
